import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AddPerson from "./AddPerson";

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
};

const extractIdent = (p) =>
  firstNonEmpty(
    p?.identificacion,
    p?.ruc,
    p?.cedula,
    p?.identificacion_cliente,
    p?.numero_identificacion,
    p?.id
  );

const extractName = (p) =>
  firstNonEmpty(
    p?.razon_social,
    p?.nombre,
    p?.nombres,
    p?.name,
    p?.cliente
  );

const buildLabelFromPerson = (p) => {
  const id = extractIdent(p);
  const name = extractName(p);
  if (id && name) return `${id} - ${name}`;
  return name || id || "";
};

const normalizeToOption = (val) => {
  if (!val) return null;

  if (typeof val === "string") {
    const t = val.trim();
    if (!t) return null;
    return { id: t, label: t, data: null };
  }

  if (typeof val === "object") {
    if ("label" in val && "data" in val) {
      const label = String(val.label || "").trim();
      const data = val.data || null;
      const id = String(val.id || extractIdent(data) || label || "").trim();

      if (!label && data) {
        const l2 = buildLabelFromPerson(data);
        if (!l2) return null;
        return { id: id || l2, label: l2, data };
      }

      if (!label) return null;
      return { id: id || label, label, data };
    }

    const data = val;
    const label = buildLabelFromPerson(data);
    if (!label) return null;
    const id = String(data?.id || extractIdent(data) || label).trim();
    return { id, label, data };
  }

  return null;
};

export default function PersonaAutocomplete({ value, onChange, tipoDocumento }) {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const lastTipoRef = useRef(tipoDocumento);
  const lastEmittedRef = useRef("");

  const incoming = useMemo(() => normalizeToOption(value), [value]);
  const isProviderMode = String(tipoDocumento || "").toUpperCase() === "PROVEEDOR";

  useEffect(() => {
    if (lastTipoRef.current !== tipoDocumento) {
      lastTipoRef.current = tipoDocumento;
      setPersonas([]);
      setLoading(false);
      setSelected(null);
      setInputValue("");
      setOpen(false);
      lastEmittedRef.current = "";
      onChange?.(null);
    }
  }, [tipoDocumento, onChange]);

  useEffect(() => {
    const next = incoming;

    if (!next) {
      setSelected(null);
      setInputValue("");
      setOpen(false);
      lastEmittedRef.current = "";
      return;
    }

    setSelected(next);
    setInputValue(String(next.label || ""));
    setOpen(false);

    const data = next.data;
    const key = String(extractIdent(data) || data?.id || next.id || "").trim();
    if (data && key && lastEmittedRef.current !== key) {
      lastEmittedRef.current = key;
      onChange?.(data);
    }
  }, [incoming, onChange]);

  const buscarPersonas = async (texto) => {
    const q = String(texto || "").trim();
    if (!q || q.length < 2 || !tipoDocumento) {
      setPersonas([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      const tipo = String(tipoDocumento).toLowerCase();
      const res = await fetch(
        `/facturas/personas/buscar/?q=${encodeURIComponent(q)}&tipo=${encodeURIComponent(tipo)}`,
        { credentials: "include" }
      );

      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) {
        setPersonas([]);
        setOpen(false);
        return;
      }

      const json = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(json?.personas)) {
        const opts = json.personas
          .map((p) => {
            const label = buildLabelFromPerson(p);
            const id = String(p?.id || extractIdent(p) || label || "").trim();
            return label ? { id, label, data: p } : null;
          })
          .filter(Boolean);

        setPersonas(opts);
        setOpen(opts.length > 0);
      } else {
        setPersonas([]);
        setOpen(false);
      }
    } catch {
      setPersonas([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setInputValue("");
    setPersonas([]);
    setSelected(null);
    setOpen(false);
    lastEmittedRef.current = "";
    onChange?.(null);
  };

  const handleCreated = (createdPerson) => {
    const option = normalizeToOption(createdPerson);
    if (!option) return;

    setSelected(option);
    setInputValue(option.label);
    setPersonas([option]);
    setOpen(false);

    const key = String(extractIdent(createdPerson) || createdPerson?.id || option.id || "").trim();
    lastEmittedRef.current = key;
    onChange?.(createdPerson);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
        <Autocomplete
          fullWidth
          options={personas}
          filterOptions={(x) => x}
          loading={loading}
          value={selected}
          inputValue={inputValue}
          open={open}
          onOpen={() => {
            if ((personas?.length || 0) > 0) setOpen(true);
          }}
          onClose={() => setOpen(false)}
          freeSolo
          disableClearable
          selectOnFocus
          clearOnBlur={false}
          handleHomeEndKeys
          onInputChange={(e, val, reason) => {
            if (reason === "reset") return;

            const nextText = String(val || "");
            setInputValue(nextText);

            if (!nextText) {
              setPersonas([]);
              setSelected(null);
              setOpen(false);
              lastEmittedRef.current = "";
              onChange?.(null);
              return;
            }

            if (nextText.length >= 2) {
              buscarPersonas(nextText);
            } else {
              setPersonas([]);
              setOpen(false);
            }
          }}
          onChange={(e, option) => {
            if (!option) {
              clearAll();
              return;
            }

            const normalized = normalizeToOption(option);
            if (!normalized) {
              clearAll();
              return;
            }

            setSelected(normalized);
            setInputValue(String(normalized.label || ""));
            setPersonas([]);
            setOpen(false);

            const key = String(
              extractIdent(normalized.data) || normalized.data?.id || normalized.id || ""
            ).trim();
            lastEmittedRef.current = key || lastEmittedRef.current;

            onChange?.(normalized.data || null);
          }}
          isOptionEqualToValue={(opt, val) => String(opt?.id || "") === String(val?.id || "")}
          getOptionLabel={(option) => String(option?.label || option || "")}
          noOptionsText={`No se encontraron ${isProviderMode ? "proveedores" : "clientes"}`}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Buscar ${isProviderMode ? "proveedor" : "cliente"}...`}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#7a7f87" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    <InputAdornment position="end">
                      {!!inputValue && (
                        <IconButton onClick={clearAll} edge="end" size="small">
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  </>
                ),
              }}
              sx={{
                minWidth: 600,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#fff",
                },
              }}
            />
          )}
        />

        <Tooltip title={`Crear ${isProviderMode ? "proveedor" : "cliente"}`}>
          <IconButton
            size="medium"
            onClick={() => setModalOpen(true)}
            sx={{
              width: 44,
              height: 44,
              bgcolor: "#1976d2",
              color: "#fff",
              borderRadius: 2,
              boxShadow: "0 8px 20px rgba(25,118,210,0.22)",
              "&:hover": { bgcolor: "#1565c0" },
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <AddPerson
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        tipoDocumento={tipoDocumento}
        onCreated={handleCreated}
      />
    </>
  );
}